/**
 * 代理操作 (act-as) の状態管理ユーティリティ。
 *
 * 管理者（ADMIN）/ マネージャー（MANAGER）が、アカウント管理ページ
 * (/account) から対象ユーザー「本人と同様の操作」を行うための仕組み。
 *
 * - sessionStorage に対象ユーザー（userId / displayName）を保存する
 * - 保存中は全 API リクエストに `X-Act-As-User` ヘッダーを付与する
 *   （BE の `accounts.middleware.ActAsUserMiddleware` が対象ユーザーとして
 *   認証を差し替える）
 * - 開始・終了はフルリロード遷移にして RTK Query キャッシュを確実に
 *   リセットする
 */

const STORAGE_KEY = "soremo_act_as";

/** BE の ActAsUserMiddleware が解釈するリクエストヘッダー名 */
export const ACT_AS_HEADER = "X-Act-As-User";

export interface ActAsState {
  userId: number;
  displayName: string;
}

/** 現在の代理操作状態を返す（未設定・不正値は null） */
export const getActAs = (): ActAsState | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActAsState> | null;
    if (!parsed || typeof parsed.userId !== "number") return null;
    return {
      userId: parsed.userId,
      displayName: String(parsed.displayName ?? ""),
    };
  } catch {
    return null;
  }
};

/**
 * 代理操作を開始し、対象ページへフルリロード遷移する。
 * @param state 対象ユーザー
 * @param path 遷移先パス（例: "/projects"）
 */
export const startActAs = (state: ActAsState, path: string): void => {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.location.href = path;
};

/** 代理操作状態のみ破棄する（遷移しない） */
export const clearActAs = (): void => {
  window.sessionStorage.removeItem(STORAGE_KEY);
};

/** 代理操作を終了し、アカウント管理ページへフルリロード遷移で戻る */
export const exitActAs = (): void => {
  clearActAs();
  window.location.href = "/account";
};

/**
 * fetchBaseQuery の prepareHeaders から呼び、代理操作中なら
 * `X-Act-As-User` ヘッダーを付与する。
 */
export const applyActAsHeader = (headers: Headers): Headers => {
  const actAs = getActAs();
  if (actAs) {
    headers.set(ACT_AS_HEADER, String(actAs.userId));
  }
  return headers;
};
